import { Router, ScreenBase } from "@frui.ts/screens";
import { action, computed, observable } from "mobx";
import AppRouter from "services/appRouter";
import LocalizationService from "services/localizationService";
import ChargeTypeViewModel from "./chargeTypeViewModel";
import LicenseViewModel from "./licenseViewModel";
import FuelTypeSelectViewModel from "viewModels/common/fuelTypeSelectViewModel";
import { interfaces } from "inversify";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import chargeOrderFuelItem, { FuelTypeValidationRules } from "models/chargeOrderFuelItem";
import ChargeOrderService from "services/chargeOrderService";
import { validate } from "@frui.ts/validation";
import { scrollToElement, scrollToFirstError } from "utils/scrollTo";
import ChargeTypesService from "services/chargeTypesService";
import FuelTypeLimitationReason from "entities/fuelTypeLimitationReason";
import EnumService from "services/enumService";
import FuelTypeResponse from "entities/fuelTypeResponse";

export const FuelTypeViewNavigationName = "fuel";

@Router.registerRoute({
  name: Router.Self,
  route: FuelTypeViewNavigationName,
})
export default class FuelTypeViewModel extends ScreenBase {
  public fuelTypeSelectViewModel: FuelTypeSelectViewModel;

  private unsubscriptions: Array<() => void> = [];

  @observable formElement?: HTMLFormElement;

  constructor(
    public chargeOrderFuelItem: chargeOrderFuelItem,
    public localizationService: LocalizationService,
    public enumService: EnumService,
    private router: AppRouter,
    private chargeOrderService: ChargeOrderService,
    private chargeTypesService: ChargeTypesService,
    private fuelTypeSelectViewModelFactory: ReturnType<typeof FuelTypeSelectViewModel.Factory>
  ) {
    super();
    this.navigationName = FuelTypeViewNavigationName;
    this.nameValue = "eshop.title";
    this.fuelTypeSelectViewModel = this.fuelTypeSelectViewModelFactory(this.chargeOrderFuelItem);
    attachAutomaticBlurValidator(this.chargeOrderFuelItem, FuelTypeValidationRules);
  }

  protected onInitialize(): Promise<any> | void {
    return super.onInitialize();
  }

  protected onActivate() {
    document.addEventListener("mousedown", this.clickOutside);
    if (this.stepper) {
      scrollToElement(this.stepper);
    }
    this.checkFuelTypeLimitation();
    this.checkFuelType();
    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean) {
    document.removeEventListener("mousedown", this.clickOutside);
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @computed
  get fuelTypeLimitation() {
    return this.chargeOrderFuelItem._fuelTypeLimitation?.limitationReason;
  }

  @computed
  get fuelType() {
    const fuelTypes = this.enumService.values("fuel_types") as FuelTypeResponse[];
    return fuelTypes.find(fuelType => fuelType.id === this.chargeOrderFuelItem.fuelTypeId)?.name ?? "";
  }

  @computed
  get showInfoAboutVehicles() {
    const countryId = this.chargeOrderService.chargeOrderItem.countryId;
    const isCzech = this.enumService.values("countries").find(country => country.code === "CZ" && country.id === countryId);
    return (
      this.enumService.values("countries").length > 0 &&
      !this.chargeOrderFuelItem._fuelTypeLimitation?.limitationReason &&
      !isCzech
    );
  }

  @computed
  get stepper() {
    return window.document.getElementById("stepper");
  }

  @action.bound
  clickOutside() {
    // Inside of the view model, for some reason the onActivate doesn't get triggered.
    this.fuelTypeSelectViewModel.clickOutside();
  }

  @action.bound
  translate(code: string, secondCode?: string) {
    return this.localizationService.translateGeneral(
      secondCode ? `eshop.order.${this.navigationName}.${code}.${secondCode}` : `eshop.order.${this.navigationName}.${code}`,
      undefined,
      true // Important for markdown links to function correctly
    );
  }

  @action.bound
  translateFuelTypeLimitation(code: string) {
    return this.translate("fuelTypeLimitation", code);
  }

  @action.bound
  translateInfoAboutVehicle(code: string) {
    return this.translate("infoAboutVehicle", code);
  }

  @action.bound
  translateFuelTypeLimitationLink(code: string) {
    if (code === FuelTypeLimitationReason.DerivedFromVehicleRegisterData) {
      return {
        text: this.translate("fuelTypeLimitation", "REGISTER_LINK"),
        link: this.translate("fuelTypeLimitation", "REGISTER_LINK_URL"),
      };
    }
    return {
      text: this.translate("fuelTypeLimitation", "ECO_LINK"),
      link: this.translate("fuelTypeLimitation", "ECO_LINK_URL"),
    };
  }

  @action.bound
  private checkFuelTypeLimitation() {
    const chargeOrderItem = this.chargeOrderService.chargeOrderItem;
    if (chargeOrderItem._fuelTypeLimitation) {
      this.chargeOrderFuelItem.fuelTypeId = chargeOrderItem?._fuelTypeLimitation.allowedFuelTypeId;
      this.chargeOrderFuelItem._fuelTypeLimitation = chargeOrderItem?._fuelTypeLimitation;
    } else {
      this.chargeOrderFuelItem._fuelTypeLimitation = undefined;
    }
  }

  @action.bound
  private checkFuelType() {
    const chargeOrderItem = this.chargeOrderService.chargeOrderItem;
    if (!chargeOrderItem._fuelTypeLimitation) {
      this.chargeOrderFuelItem.fuelTypeId = chargeOrderItem?._fuelTypeId;
    }
  }

  @action.bound
  private updateChargeOrderItem() {
    const chargeOrderItem = this.chargeOrderService.chargeOrderItem;
    if (this.chargeOrderFuelItem.fuelTypeId) {
      chargeOrderItem._fuelTypeId = this.chargeOrderFuelItem.fuelTypeId;
    }
    chargeOrderItem._priceListItems = this.chargeTypesService.chargePriceList?.items.filter(
      item => item.fuelTypeId === this.chargeOrderFuelItem.fuelTypeId
    );
    this.chargeOrderService.persistChargeOrder();
  }

  @action.bound
  public onContinue() {
    if (!this.validateAll()) {
      return;
    }
    this.updateChargeOrderItem();
    this.router.navigate(ChargeTypeViewModel);
  }

  @action.bound
  public onBack() {
    this.updateChargeOrderItem();
    this.router.navigate(LicenseViewModel);
  }

  @action.bound
  private validateAll() {
    const isValid = validate(this.chargeOrderFuelItem);
    if (!isValid) {
      scrollToFirstError(this.formElement);
    }

    return isValid;
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeOrderFuelItem: chargeOrderFuelItem) =>
      new FuelTypeViewModel(
        chargeOrderFuelItem,
        container.get(LocalizationService),
        container.get(EnumService),
        container.get(AppRouter),
        container.get(ChargeOrderService),
        container.get(ChargeTypesService),
        container.get(FuelTypeSelectViewModel.Factory)
      );
  }
}
